import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPlanManifest } from '../../../../lib/api/epics';
import type { EpicDetail, PlanManifestStory } from '../../../../../../shared/types/epics';
import { Card, CardHeader, CardBody } from '../../../../components/ui';

// ---------------------------------------------------------------------------
// FORGE-055 — Plan review SP preview (FR-1) — only when state = PLAN_POSTED
// ---------------------------------------------------------------------------

export const PlanReviewCard: React.FC<{ epic: EpicDetail }> = ({ epic }) => {
  const [org, repo] = epic.repo.split('/');
  const q = useQuery({
    queryKey: ['plan-manifest', epic.repo, epic.number],
    queryFn: () => fetchPlanManifest(org, repo, epic.number),
    enabled: Boolean(org && repo),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold text-slate-900">Proposed plan</div>
          <span
            className="text-slate-400 cursor-help text-xs"
            title="Fibonacci complexity estimate (1 = trivial, 21 = very complex). Used to compute cost per story point at epic completion."
            aria-label="What are story points?"
          >
            ⓘ
          </span>
        </div>
      </CardHeader>
      <CardBody>
        {q.isLoading && <div className="text-sm text-slate-500">Loading plan…</div>}
        {q.isError && (
          <div role="alert" className="text-sm text-danger-600">
            {(q.error as Error).message}
          </div>
        )}
        {q.isSuccess && !q.data.found && (
          <div className="text-sm text-slate-500">
            The planner hasn't posted a story-point manifest for this epic yet.
          </div>
        )}
        {q.isSuccess && q.data.found && (
          <PlanManifestTable
            stories={q.data.stories}
            total={q.data.totalStoryPoints}
          />
        )}
      </CardBody>
    </Card>
  );
};

export const PlanManifestTable: React.FC<{
  stories: PlanManifestStory[];
  total: number | null;
}> = ({ stories, total }) => (
  <table className="w-full text-sm" data-testid="plan-manifest-table">
    <thead className="border-b border-slate-200">
      <tr className="text-left">
        <th className="py-2 text-2xs uppercase tracking-wide text-slate-500 font-semibold">#</th>
        <th className="py-2 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Story</th>
        <th className="py-2 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Agents</th>
        <th className="py-2 text-2xs uppercase tracking-wide text-slate-500 font-semibold text-right">SP</th>
      </tr>
    </thead>
    <tbody>
      {stories.map((s) => (
        <tr key={s.index} className="border-b border-slate-100 last:border-b-0">
          <td className="py-2 font-mono text-xs text-slate-500">{s.index}</td>
          <td className="py-2 text-slate-900">{s.title}</td>
          <td className="py-2 text-xs text-slate-600">{s.agents.join(', ') || '—'}</td>
          <td className="py-2 text-right tabular-nums font-medium">
            {s.storyPoints !== null ? s.storyPoints : <span className="text-slate-400">—</span>}
          </td>
        </tr>
      ))}
    </tbody>
    <tfoot>
      <tr className="border-t border-slate-200">
        <td colSpan={3} className="py-2 text-xs text-slate-600 font-medium">
          {stories.length} {stories.length === 1 ? 'story' : 'stories'}
        </td>
        <td className="py-2 text-right tabular-nums font-semibold text-slate-900">
          {total !== null ? `${total} SP total` : '—'}
        </td>
      </tr>
    </tfoot>
  </table>
);
